import { useCallback, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { LiveDataBridge } from '../app/live-data';
import { Drawer } from '../ui/design-system';
import { Icon } from '../ui/icons';
import type { IconName } from '../ui/icons';

export type NavigationItem = {
  path: string;
  label: string;
  shortLabel?: string;
  icon: IconName;
  group: 'work' | 'knowledge' | 'settings';
  mobile?: boolean;
  ownerOnly?: boolean;
  directionOnly?: boolean;
};

type ApplicationShellProps = { navigationItems: readonly NavigationItem[] };

const groupLabels = { work: 'Piloter', knowledge: 'Organiser', settings: 'Réglages' } as const;

export function ApplicationShell({ navigationItems }: ApplicationShellProps) {
  const { member } = useAuth();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Le raccourci n'a plus d'utilité sur la page qui porte déjà l'enregistreur.
  const showVoiceShortcut = !pathname.startsWith('/inbox');
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const isOwner = member?.organization_roles?.is_owner === true;
  const isDirection = isOwner || member?.organization_roles?.is_secretary === true;
  const visibleItems = navigationItems.filter(
    (item) => (!item.ownerOnly || isOwner) && (!item.directionOnly || isDirection),
  );
  const mobileItems = visibleItems.filter((item) => item.mobile);

  const navigation = (
    <nav aria-label="Navigation principale">
      {(Object.keys(groupLabels) as Array<keyof typeof groupLabels>).map((group) => {
        const items = visibleItems.filter((item) => item.group === group);
        if (items.length === 0) return null;
        return (
          <section className="nav-group" key={group} aria-labelledby={`nav-${group}`}>
            <h2 id={`nav-${group}`}>{groupLabels[group]}</h2>
            <ul className="navigation-list">
              {items.map((item) => (
                <li key={item.path}>
                  <NavLink to={item.path} onClick={closeMenu}>
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </nav>
  );

  return (
    <div className="application-shell">
      <a className="skip-link" href="#main-content">
        Aller au contenu
      </a>
      <aside className="sidebar">
        <Link className="brand" to="/aujourdhui" aria-label="ALBA Direction, accueil">
          <span className="brand-symbol">A</span>
          <span>
            <strong>ALBA</strong>
            <small>Direction</small>
          </span>
        </Link>
        {navigation}
        <Link className="account-chip" to="/mon-compte">
          <span className="account-avatar" aria-hidden="true">
            {member?.profiles?.display_name.charAt(0)}
          </span>
          <span>
            <strong>{member?.profiles?.display_name}</strong>
            <small>{member?.organization_roles?.label}</small>
          </span>
          <Icon name="arrow" />
        </Link>
      </aside>

      <header className="mobile-header">
        <Link className="brand" to="/aujourdhui" aria-label="ALBA Direction, accueil">
          <span className="brand-symbol">A</span>
          <strong>ALBA</strong>
        </Link>
        <button
          type="button"
          className="icon-button"
          onClick={() => setMenuOpen(true)}
          aria-label="Ouvrir le menu"
        >
          <Icon name="menu" />
        </button>
      </header>

      <main id="main-content" className="main-content">
        <LiveDataBridge />
        <Outlet />
      </main>

      {showVoiceShortcut && (
        <Link
          className="voice-capture-button"
          to="/inbox?capture=vocal"
          aria-label="Dicter un vocal à Alba"
          title="Dicter un vocal à Alba"
        >
          <Icon name="microphone" />
          <span className="voice-capture-label">Dicter un vocal</span>
        </Link>
      )}
      <nav className="mobile-tabbar" aria-label="Navigation mobile">
        {mobileItems.map((item) => (
          <NavLink key={item.path} to={item.path}>
            <Icon name={item.icon} />
            <span>{item.shortLabel ?? item.label}</span>
          </NavLink>
        ))}
      </nav>

      <Drawer open={menuOpen} title="Navigation" onClose={closeMenu}>
        <div className="mobile-menu-brand">
          <span className="brand-symbol">A</span>
          <div>
            <strong>{member?.profiles?.display_name}</strong>
            <small>{member?.organization_roles?.label}</small>
          </div>
        </div>
        {navigation}
      </Drawer>
    </div>
  );
}
